import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { useCallback, useEffect, useState } from 'react';
import {
  AttendanceWrapper,
  AttendanceResultWrapper,
} from '../router/user/services/Attendance/type';

function getExcelColumnName(index: number): string {
  let columnName = '';
  while (index >= 0) {
    columnName = String.fromCharCode((index % 26) + 65) + columnName;
    index = Math.floor(index / 26) - 1;
  }
  return columnName;
}

export const createExcelFileWithColumnLetters = (
  data: any[],
  columns: { header: string; key: string; columnLetter: string }[],
  fileName: string
) => {
  console.log('data', data);
  console.log('columns', columns)
  // Step 1: 워크시트 객체 초기화
  const worksheet: XLSX.WorkSheet = {};

  // Step 2: 헤더 설정
  columns.forEach(({ header, columnLetter }) => {
    worksheet[`${columnLetter}1`] = { v: header }; // 첫 행에 헤더 추가
  });

  // Step 3: 데이터 설정
  data.forEach((row, rowIndex) => {
    columns.forEach(({ key, columnLetter }) => {
      worksheet[`${columnLetter}${rowIndex + 2}`] = { v: row[key] || '' }; // 데이터 추가
    });
  });

  // Step 4: 워크시트의 범위 설정
  const range = {
    s: { c: 0, r: 0 }, // 시작 (A1)
    e: { c: columns.length - 1, r: data.length + 1 }, // 끝
  };
  worksheet['!ref'] = XLSX.utils.encode_range(range);

  // Step 5: 워크북 생성 및 저장
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
};

export const useAttendanceExcelDownload = () => {
  const generate = async (
    attendanceList: AttendanceWrapper[], // attendanceList 배열
    resultList: AttendanceResultWrapper[]
  ) => {
    const workbook = new ExcelJS.Workbook();

    let resultStartIndex = 0;

    // ** attendanceList 처리 **
    attendanceList.forEach((attendance) => {
      // 시트 생성
      const sheet = workbook.addWorksheet(attendance.name); // 이름을 시트 이름으로 설정

      sheet.pageSetup = {
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 1,
        paperSize: 9, // A4
        orientation: 'landscape', // 👈 가로 방향
        margins: {
          left: 0.2,
          right: 0.2,
          top: 0.4,
          bottom: 0.4,
          header: 0.3,
          footer: 0.3,
        },
      };

      // ** 스타일 및 병합 영역 **
      // A1~G3 병합 및 텍스트, 스타일 설정 (맑은 고딕 Bold)
      sheet.mergeCells('A1:G2');
      const titleText =
        attendance.year + '년' + ' ' + attendance.month + '월' + ' 출퇴근 결과';
      const titleCell = sheet.getCell('A1');
      titleCell.value = titleText;
      titleCell.font = { name: 'Malgun Gothic', bold: true, size: 28 };
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };

      // H1~H2 병합 및 '결재' 입력 (맑은 고딕 Bold)
      sheet.mergeCells('H1:H2');
      const approvalCell = sheet.getCell('H1');
      approvalCell.value = '결재';
      approvalCell.font = { name: 'Malgun Gothic', bold: true, size: 12 };
      approvalCell.alignment = { vertical: 'middle', horizontal: 'center' };

      // I1, J1, K1에 텍스트 입력 (맑은 고딕 Bold)
      const i1Cell = sheet.getCell('I1');
      i1Cell.value = '담 당';
      i1Cell.font = { name: 'Malgun Gothic', bold: true, size: 12 };
      i1Cell.alignment = { vertical: 'middle', horizontal: 'center' };

      const j1Cell = sheet.getCell('J1');
      j1Cell.value = '사무국장';
      j1Cell.font = { name: 'Malgun Gothic', bold: true, size: 12 };
      j1Cell.alignment = { vertical: 'middle', horizontal: 'center' };

      const k1Cell = sheet.getCell('K1');
      k1Cell.value = '원 장';
      k1Cell.font = { name: 'Malgun Gothic', bold: true, size: 12 };
      k1Cell.alignment = { vertical: 'middle', horizontal: 'center' };

      const 근무일자셀 = sheet.getCell('A4');
      근무일자셀.value = '근무일자';

      const 이름셀 = sheet.getCell('B4');
      이름셀.value = '이름';

      const 근무스케줄셀 = sheet.getCell('C4');
      근무스케줄셀.value = '근무스케줄';

      const 출근시간셀 = sheet.getCell('D4');
      출근시간셀.value = '출근시간';

      const 퇴근시간셀 = sheet.getCell('E4');
      퇴근시간셀.value = '퇴근시간';

      const 출근판정셀 = sheet.getCell('F4');
      출근판정셀.value = '출근판정';

      const 퇴근판정셀 = sheet.getCell('G4');
      퇴근판정셀.value = '퇴근판정';

      const 휴일근무시간셀 = sheet.getCell('H4');
      휴일근무시간셀.value = '휴일근무시간';

      const 정상근무시간셀 = sheet.getCell('I4');
      정상근무시간셀.value = '정상근무시간';

      const 수정사유셀 = sheet.getCell('J4');
      수정사유셀.value = '수정 사유';

      const 연장근무시간셀 = sheet.getCell('K4');
      연장근무시간셀.value = '연장근무시간';

      const headerRange = [
        'A4',
        'B4',
        'C4',
        'D4',
        'E4',
        'F4',
        'G4',
        'H4',
        'I4',
        'J4',
        'K4',
      ];
      headerRange.forEach((cellAddress) => {
        const cell = sheet.getCell(cellAddress);
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'double' },
          right: { style: 'thin' },
        };
        // 배경색 설정
        cell.fill = {
          type: 'pattern', // 패턴으로 배경색을 설정
          pattern: 'solid', // 채우기 유형 설정
          fgColor: { argb: 'f2f2f2' }, // 배경색
        };
        cell.font = { name: 'Malgun Gothic', bold: true, size: 11 };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      // 2행 높이 설정
      sheet.getRow(1).height = 21;
      sheet.getRow(2).height = 60;
      sheet.getRow(4).height = 22;
      sheet.getColumn('A').width = 12;
      sheet.getColumn('B').width = 9.5;
      sheet.getColumn('C').width = 14;
      sheet.getColumn('D').width = 21;
      sheet.getColumn('E').width = 21;
      sheet.getColumn('F').width = 11.5;
      sheet.getColumn('G').width = 11.5;
      sheet.getColumn('H').width = 16.5;
      sheet.getColumn('I').width = 16.5;
      sheet.getColumn('J').width = 22;
      sheet.getColumn('K').width = 22;

      // H1~K2 범위에 대해 테두리 설정
      const range = ['H1', 'I1', 'J1', 'K1', 'H2', 'I2', 'J2', 'K2'];
      range.forEach((cellAddress) => {
        const cell = sheet.getCell(cellAddress);
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });

      // ** 데이터 작성 (A5~K{rowIndex}) **
      let rowIndex = 5; // 데이터 시작 행
      attendance.data.forEach((data, index) => {
        const row = sheet.getRow(rowIndex);
        row.height = 17;
        row.getCell(1).value = data.date;
        row.getCell(2).value = data.name;
        row.getCell(3).value = data.schedule;
        row.getCell(4).value = data.clockInTime;
        row.getCell(5).value = data.clockOutTime;
        row.getCell(6).value = data.clockInStatus;
        row.getCell(7).value = data.clockOutStatus;
        row.getCell(8).value = data.holidayWorkTime;
        row.getCell(9).value = data.workdayWorkTime;
        row.getCell(10).value = data.changeReason;
        row.getCell(11).value = data.overWorkTime;

        // 모든 셀에 대해 테두리, 폰트 및 정렬 적용
        for (let col = 1; col <= 11; col++) {
          const cell = row.getCell(col);

          // 데이터가 비어있는 셀도 테두리 설정
          if (!cell.value) {
            cell.value = ''; // 빈 값일 경우에도 빈 문자열로 설정 (엑셀에서 빈 셀로 처리되도록)
          }

          cell.font = { name: 'Calibri', size: 12 };
          cell.alignment = {
            vertical: 'middle',
            horizontal: 'center',
            shrinkToFit: true,
          };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          };
        }

        if (index + 1 === attendance.data.length) {
          const targetResult = resultList.find(
            (result) => result.name === attendance.name
          );
          if (resultStartIndex === 0) resultStartIndex = rowIndex + 2;
          //헤더
          const 근무일수셀 = sheet.getCell(`D${resultStartIndex}`);
          근무일수셀.value = '근무일수(일)';

          const 근무단축셀 = sheet.getCell(`E${resultStartIndex}`);
          근무단축셀.value = '근무단축(시간)';

          sheet.mergeCells(`F${resultStartIndex}:G${resultStartIndex}`);
          const 연가셀 = sheet.getCell(`F${resultStartIndex}`);
          연가셀.value = '연가';

          const 연장근로셀 = sheet.getCell(`H${resultStartIndex}`);
          연장근로셀.value = '연장근로(시간)';

          const 유급휴일무급처리셀 = sheet.getCell(`I${resultStartIndex}`);
          유급휴일무급처리셀.value = '유급휴일무급처리(시간)';

          const 결근셀 = sheet.getCell(`J${resultStartIndex}`);
          결근셀.value = '결근(일)';

          const 병가셀 = sheet.getCell(`K${resultStartIndex}`);
          병가셀.value = '병가';

          //밸류
          const 근무일수값셀 = sheet.getCell(`D${resultStartIndex + 1}`);
          근무일수값셀.value = targetResult?.data.totalWorkDays;

          const 근무단축값셀 = sheet.getCell(`E${resultStartIndex + 1}`);
          근무단축값셀.value = targetResult?.data.shortenedWorkHours;

          sheet.mergeCells(`F${resultStartIndex + 1}:G${resultStartIndex + 1}`);
          const 연가값셀 = sheet.getCell(`F${resultStartIndex + 1}`);
          연가값셀.value = targetResult?.data.annualLeaveHours;

          const 연장근로값셀 = sheet.getCell(`H${resultStartIndex + 1}`);
          연장근로값셀.value = targetResult?.data.overtimeHours;

          const 유급휴일무급처리값셀 = sheet.getCell(
            `I${resultStartIndex + 1}`
          );
          유급휴일무급처리값셀.value = targetResult?.data.paidToUnpaidHours;

          const 결근값셀 = sheet.getCell(`J${resultStartIndex + 1}`);
          결근값셀.value = targetResult?.data.absentDays;

          const 병가값셀 = sheet.getCell(`K${resultStartIndex + 1}`);
          병가값셀.value = targetResult?.data.sickLeaveDays;

          const range = [
            `D${resultStartIndex}`,
            `E${resultStartIndex}`,
            `F${resultStartIndex}`,
            `H${resultStartIndex}`,
            `I${resultStartIndex}`,
            `J${resultStartIndex}`,
            `K${resultStartIndex}`,
            `D${resultStartIndex + 1}`,
            `E${resultStartIndex + 1}`,
            `F${resultStartIndex + 1}`,
            `H${resultStartIndex + 1}`,
            `I${resultStartIndex + 1}`,
            `J${resultStartIndex + 1}`,
            `K${resultStartIndex + 1}`,
          ];
          range.forEach((cellAddress) => {
            const cell = sheet.getCell(cellAddress);
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' },
            };
            if (cellAddress === `I${resultStartIndex}`) {
              cell.font = { name: 'Malgun Gothic', bold: true, size: 6 };
              cell.alignment = { vertical: 'middle', horizontal: 'center' };
            } else {
              cell.font = { name: 'Malgun Gothic', bold: true, size: 11 };
              cell.alignment = { vertical: 'middle', horizontal: 'center' };
            }
          });
        } else {
          rowIndex++;
        }
      });
    });

    // ** 엑셀 파일 저장 및 다운로드 **
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'attendance_report.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return { generate };
};

export const useExcelDownload = () => {
  const generate = async (
    data: any[],
    columns: {
      header: string;
      key: string;
      columnLetter: string;
      width: number;
    }[],
    fileName: string
  ) => {
    const workbook = new ExcelJS.Workbook();

    // 시트 생성
    const sheet = workbook.addWorksheet(fileName); // 이름을 시트 이름으로 설정

    const rowStartIndex = 1;

    columns.forEach((column, index) => {
      const titleText = column.header;
      const cellCol = getExcelColumnName(index);
      const cellAddress = `${cellCol}${rowStartIndex}`;
      const titleCell = sheet.getCell(cellAddress);
      titleCell.value = titleText;
      titleCell.font = { name: 'Malgun Gothic', bold: true, size: 11 };
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
      titleCell.fill = {
        type: 'pattern', // 패턴으로 배경색을 설정
        pattern: 'solid', // 채우기 유형 설정
        fgColor: { argb: 'f2f2f2' }, // 배경색
      };
      titleCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
      sheet.getColumn(cellCol).width = column.width;
    });

    sheet.getRow(rowStartIndex).height = 25;

    data.forEach((row, rowIndex) => {
      columns.forEach(({ key, columnLetter }) => {
        const currentIndex = rowStartIndex + rowIndex + 1;
        const bodyCell = sheet.getCell(`${columnLetter}${currentIndex}`);
        sheet.getRow(currentIndex).height = 18;
        bodyCell.font = { name: 'Malgun Gothic', bold: true, size: 10 };
        bodyCell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        if (columnLetter === 'A') {
          bodyCell.value = rowIndex + 1;
        } else {
          bodyCell.value = row[key];
        }
        // 특정 열(예: 금액 관련 열)인 경우 원화 형식 적용
        if (key === 'outPrice') {
          bodyCell.numFmt = '#,##0'; // 1,000 단위 쉼표 & 원화(₩) 표시
        }
        bodyCell.alignment = { vertical: 'middle', horizontal: 'center' };
      });
    });

    // ** 엑셀 파일 저장 및 다운로드 **
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'attendance_report.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return { generate };
};

// useAttendanceExcelDownload 훅
// export const useAttendanceExcelDownload = () => {
//   const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
//   const [templateSheet, setTemplateSheet] = useState<XLSX.WorkSheet | null>(
//     null
//   );

//   // 양식 파일 로드
//   const generate = (
//     attendanceList: AttendanceWrapper[],
//     resultList: AttendanceResultWrapper[]
//   ) => {
//     if (!workbook || !templateSheet) return;

//     const newWorkbook = XLSX.utils.book_new();

//     let resultStartIndex = -1;
//     attendanceList.forEach((attendance, index) => {
//       // workbook.Sheets[attendance.name] = { ...templateSheet };
//       // workbook.SheetNames.push(attendance.name);
//       // const newSheet = XLSX.utils.aoa_to_sheet([]);
//       // const newSheet = templateSheet;
//       const newSheet: XLSX.WorkSheet = JSON.parse(
//         JSON.stringify(templateSheet)
//       );
//       let rowIndex = 5; // 데이터 시작 인덱스
//       let dataStartIndex = 5;
//       attendance.data.forEach((data, index) => {
//         // A1 ~ G3 병합, 폰트 스타일 및 텍스트 설정
//         newSheet['!merges'] = [
//           { s: { r: 0, c: 0 }, e: { r: 2, c: 6 } }, // A1 ~ G3 병합
//           { s: { r: 0, c: 7 }, e: { r: 1, c: 7 } }, // H1 ~ H2 병합
//         ];
//         newSheet['A1'] = {
//           v: '사용자 지정 텍스트', // 임의 텍스트
//           s: {
//             font: { bold: true, sz: 28 }, // 볼드체, 폰트 크기 28
//             alignment: { vertical: 'center', horizontal: 'center' }, // 가운데 정렬
//           },
//         };

//         // 이하 항목들 스타일 설정
//         newSheet['H1'] = {
//           v: '결재',
//           s: {
//             font: { bold: true, sz: 12 },
//             alignment: { vertical: 'center', horizontal: 'center' },
//           },
//         };
//         newSheet['I1'] = {
//           v: '담 당',
//           s: {
//             font: { bold: true, sz: 12 },
//             alignment: { vertical: 'center', horizontal: 'center' },
//           },
//         };
//         newSheet['J1'] = {
//           v: '사무국장',
//           s: {
//             font: { bold: true, sz: 12 },
//             alignment: { vertical: 'center', horizontal: 'center' },
//           },
//         };
//         newSheet['K1'] = {
//           v: '원 장',
//           s: {
//             font: { bold: true, sz: 12 },
//             alignment: { vertical: 'center', horizontal: 'center' },
//           },
//         };

//         // 2행 높이 설정
//         newSheet['!rows'] = [];
//         newSheet['!rows'][1] = { hpx: 60 }; // 2행 높이 60px
//         newSheet[`A${rowIndex}`] = {
//           ...newSheet[`A${rowIndex}`],
//           v: data.date,
//         };
//         newSheet[`B${rowIndex}`] = {
//           ...newSheet[`B${rowIndex}`],
//           v: data.name,
//         };
//         newSheet[`C${rowIndex}`] = {
//           ...newSheet[`C${rowIndex}`],
//           v: data.schedule,
//         };
//         newSheet[`D${rowIndex}`] = {
//           ...newSheet[`D${rowIndex}`],
//           v: data.clockInTime,
//         };
//         newSheet[`E${rowIndex}`] = {
//           ...newSheet[`E${rowIndex}`],
//           v: data.clockOutTime,
//         };
//         newSheet[`F${rowIndex}`] = {
//           ...newSheet[`F${rowIndex}`],
//           v: data.clockInStatus,
//         };
//         newSheet[`G${rowIndex}`] = {
//           ...newSheet[`G${rowIndex}`],
//           v: data.clockOutStatus,
//         };
//         newSheet[`H${rowIndex}`] = {
//           ...newSheet[`H${rowIndex}`],
//           v: data.holidayWorkTime,
//         };
//         newSheet[`I${rowIndex}`] = {
//           ...newSheet[`I${rowIndex}`],
//           v: data.workdayWorkTime,
//         };
//         newSheet[`J${rowIndex}`] = {
//           ...newSheet[`J${rowIndex}`],
//           v: data.changeReason,
//         };
//         newSheet[`K${rowIndex}`] = {
//           ...newSheet[`K${rowIndex}`],
//           v: data.overWorkTime,
//         };
//         rowIndex++;
//         // if (index + 1 === attendance.data.length) {
//         //   if (resultStartIndex === -1) resultStartIndex = rowIndex + 2; // 1칸 간격을 두고 결과값 행 인덱스 설정

//         //   // 바깥 외곽선만 추가 (A4부터 K{rowIndex}까지)
//         //   const startRow = 4;
//         //   const endRow = rowIndex;
//         //   const startCol = 'A';
//         //   const endCol = 'K';

//         //   // 바깥 외곽선 추가
//         //   for (let r = startRow; r <= endRow; r++) {
//         //     for (let c = 1; c <= 11; c++) {
//         //       const cellRef = `${String.fromCharCode(65 + c - 1)}${r}`;

//         //       // 첫 번째 행과 마지막 행, 첫 번째 열과 마지막 열에만 외곽선 추가
//         //       if (r === startRow || r === endRow || c === 1 || c === 11) {
//         //         newSheet[cellRef] = {
//         //           ...newSheet[cellRef], // 기존 셀 내용 복사
//         //           s: {
//         //             border: {
//         //               top: { style: 'thin' },
//         //               left: { style: 'thin' },
//         //               right: { style: 'thin' },
//         //               bottom: { style: 'thin' },
//         //             },
//         //           },
//         //         };
//         //       }
//         //     }
//         //   }
//         // }
//       });

//       // 시트의 데이터 범위 설정 (A5부터 K{rowIndex-1}까지)
//       newSheet['!ref'] = `A1:K${rowIndex - 1}`;

//       XLSX.utils.book_append_sheet(newWorkbook, newSheet, attendance.name);
//     });

//     console.log('workbook', newWorkbook);
//     // 엑셀 파일 다운로드
//     XLSX.writeFile(newWorkbook, 'attendance_report.xlsx');
//   };
//   // [workbook, templateSheet] // workbook과 templateSheet이 변경될 때만 재실행

//   // 양식 파일 로드
//   const loadTemplate = async () => {
//     const response = await fetch('/excel_template/attendance_template.xlsx');
//     const arrayBuffer = await response.arrayBuffer();
//     const wb: XLSX.WorkBook = XLSX.read(arrayBuffer, { type: 'array' });
//     setWorkbook(wb);

//     const originalWorksheet = wb.Sheets[wb.SheetNames[0]];
//     const templateSheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(
//       XLSX.utils.sheet_to_json(originalWorksheet, { header: 1 }) as any[][]
//     );
//     setTemplateSheet(templateSheet);
//   };

//   useEffect(() => {
//     loadTemplate();
//   }, []);

//   return { generate };
// };
